/**
 * Performance Monitoring Service
 * Tracks Core Web Vitals and reports them to analytics
 * 
 * Core Web Vitals tracked:
 * - CLS (Cumulative Layout Shift)
 * - LCP (Largest Contentful Paint)
 * - INP (Interaction to Next Paint) - replaces FID in web-vitals v3+
 * - FCP (First Contentful Paint)
 * - TTFB (Time to First Byte)
 * 
 * USAGE:
 * Call initWebVitals() once at app startup (after analytics init)
 */

import type { Metric } from "web-vitals";
import { trackEvent } from "@/services/analyticsService";
import { isAnalyticsEnabled, isAnalyticsDebugMode } from "@/config/analyticsConfig";

/**
 * Web vital metric with rating
 */
export interface WebVitalMetric {
  /** Metric name (CLS, FID, LCP, etc.) */
  name: string;
  /** Unique ID for this metric instance */
  id: string;
  /** Metric value */
  value: number;
  /** Rating: good, needs-improvement, or poor */
  rating: "good" | "needs-improvement" | "poor";
  /** Delta from previous value */
  delta?: number;
  /** Navigation type */
  navigationType?: string;
}

/**
 * Report a web vital metric
 * Sends the metric to analytics for tracking
 * 
 * @param metric - Web vital metric to report
 */
export function reportWebVital(metric: WebVitalMetric): void {
  // Debug logging
  if (isAnalyticsDebugMode()) {
    if (import.meta.env.DEV) console.info("[Performance]", metric.name, {
      value: metric.value,
      rating: metric.rating,
      id: metric.id,
    });
  }

  // Skip if analytics not enabled
  if (!isAnalyticsEnabled()) {
    return;
  }

  // Track as analytics event
  trackEvent("user_action", {
    action: "performance_metric",
    metric_name: metric.name,
    metric_value: Math.round(metric.value),
    metric_rating: metric.rating,
    metric_id: metric.id,
    metric_delta: metric.delta ? Math.round(metric.delta) : undefined,
    navigation_type: metric.navigationType,
  });
}

/**
 * Calculate performance rating based on thresholds
 * Uses Google's Core Web Vitals thresholds
 * 
 * @param metricName - Name of the metric
 * @param value - Metric value
 * @returns Rating (good, needs-improvement, or poor)
 */
function getPerformanceRating(
  metricName: string,
  value: number
): "good" | "needs-improvement" | "poor" {
  // Thresholds from https://web.dev/vitals/
  const thresholds: Record<string, { good: number; poor: number }> = {
    CLS: { good: 0.1, poor: 0.25 },
    LCP: { good: 2500, poor: 4000 },
    INP: { good: 200, poor: 500 },
    FCP: { good: 1800, poor: 3000 },
    TTFB: { good: 800, poor: 1800 },
  };

  const threshold = thresholds[metricName];
  if (!threshold) {
    return "good"; // Default for unknown metrics
  }

  if (value <= threshold.good) {
    return "good";
  } else if (value <= threshold.poor) {
    return "needs-improvement";
  } else {
    return "poor";
  }
}

/**
 * Initialize web vitals monitoring
 * Sets up listeners for Core Web Vitals and reports them
 * 
 * This should be called once at app startup, after analytics is initialized
 */
export async function initWebVitals(): Promise<void> {
  // Skip if not in browser
  if (typeof window === "undefined") {
    return;
  }

  // Skip if analytics disabled (no point tracking without reporting)
  if (!isAnalyticsEnabled()) {
    if (isAnalyticsDebugMode()) {
      if (import.meta.env.DEV) console.info("[Performance] Web vitals tracking disabled (analytics disabled)");
    }
    return;
  }

  try {
    // Dynamic import to keep main bundle small
    const { onCLS, onLCP, onINP, onFCP, onTTFB } = await import("web-vitals");

    // Helper to convert web-vitals Metric to our WebVitalMetric
    const convertMetric = (metric: Metric): WebVitalMetric => ({
      name: metric.name,
      id: metric.id,
      value: metric.value,
      rating: getPerformanceRating(metric.name, metric.value),
      delta: metric.delta,
      navigationType: metric.navigationType,
    });

    // Set up listeners for all Core Web Vitals
    onCLS((metric: Metric) => reportWebVital(convertMetric(metric)));
    onLCP((metric: Metric) => reportWebVital(convertMetric(metric)));
    onINP((metric: Metric) => reportWebVital(convertMetric(metric)));
    onFCP((metric: Metric) => reportWebVital(convertMetric(metric)));
    onTTFB((metric: Metric) => reportWebVital(convertMetric(metric)));

    if (isAnalyticsDebugMode()) {
      if (import.meta.env.DEV) console.info("[Performance] Web vitals monitoring initialized");
    }
  } catch (error) {
    console.warn("[Performance] Failed to initialize web vitals:", error);
  }
}

/**
 * Performance budget thresholds
 * These are targets for Core Web Vitals - used for documentation
 * 
 * GOOD thresholds (75th percentile):
 * - LCP: ≤ 2.5s
 * - CLS: ≤ 0.1
 * - INP: ≤ 200ms (replaces FID)
 * - FCP: ≤ 1.8s
 * - TTFB: ≤ 800ms
 */
export const PERFORMANCE_BUDGET = {
  LCP: {
    good: 2500,
    needsImprovement: 4000,
    description: "Largest Contentful Paint - Time to render largest content element",
  },
  CLS: {
    good: 0.1,
    needsImprovement: 0.25,
    description: "Cumulative Layout Shift - Visual stability during page load",
  },
  INP: {
    good: 200,
    needsImprovement: 500,
    description: "Interaction to Next Paint - Responsiveness to user interactions",
  },
  FCP: {
    good: 1800,
    needsImprovement: 3000,
    description: "First Contentful Paint - Time to first content render",
  },
  TTFB: {
    good: 800,
    needsImprovement: 1800,
    description: "Time to First Byte - Server response time",
  },
} as const;
